// Full-screen splash + DOM title menu (centered glass panel).

import { setCanvasVisible } from '../render/display';

const SPLASH_URL = './assets/i/splash.jpg';
export const MERITOUS_VERSION = 'v 1.2';

export type MenuChoice = 'continue' | 'new' | 'wuss' | 'settings' | 'logout';

export interface MainMenuOptions {
  canContinue: boolean;
  saveMode: 'cloud' | 'offline';
  user: string | null;
}

let shell: HTMLDivElement | null = null;
let panel: HTMLDivElement | null = null;
let panelHost: HTMLDivElement | null = null;
let loadingWrap: HTMLDivElement | null = null;
let loadingBar: HTMLDivElement | null = null;
let loadingMsg: HTMLDivElement | null = null;
let versionEl: HTMLDivElement | null = null;
let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    #meritous-title-screen {
      position: fixed;
      inset: 0;
      z-index: 20;
      overflow: hidden;
      font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      color: #e8f0ff;
    }
    #meritous-title-screen .merit-splash-bg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center center;
      transform-origin: center center;
      animation: merit-splash-zoom 24s ease-in-out infinite alternate;
    }
    @keyframes merit-splash-zoom {
      from { transform: scale(1); }
      to { transform: scale(1.04); }
    }
    #meritous-title-screen .merit-splash-vignette {
      position: absolute;
      inset: 0;
      background: radial-gradient(
        ellipse 85% 70% at 50% 45%,
        rgba(4, 2, 12, 0.05) 0%,
        rgba(4, 2, 12, 0.35) 55%,
        rgba(4, 2, 12, 0.72) 100%
      );
      pointer-events: none;
    }
    #meritous-title-screen .merit-title-version {
      position: absolute;
      top: max(12px, env(safe-area-inset-top, 0px));
      left: max(16px, env(safe-area-inset-left, 0px));
      font-size: 13px;
      letter-spacing: 0.06em;
      color: rgba(200, 220, 255, 0.65);
      text-shadow: 0 1px 8px rgba(0, 0, 0, 0.8);
      pointer-events: none;
    }
    #meritous-title-screen .merit-title-panel-host {
      position: absolute;
      left: 50%;
      top: 54%;
      transform: translate(-50%, -50%);
      width: min(400px, calc(100vw - 32px));
      z-index: 2;
    }
    #meritous-title-screen .merit-title-panel {
      padding: 20px 24px;
      border-radius: 16px;
      background: rgba(12, 8, 28, 0.72);
      border: 1px solid rgba(160, 120, 255, 0.45);
      box-shadow:
        0 0 32px rgba(80, 160, 255, 0.12),
        0 8px 32px rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    #merit-settings-overlay .merit-title-panel {
      padding: 20px 24px;
      border-radius: 16px;
      background: rgba(12, 8, 28, 0.92);
      border: 1px solid rgba(160, 120, 255, 0.45);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    #meritous-title-screen .merit-title-heading,
    #merit-settings-overlay .merit-title-heading {
      text-align: center;
      font-size: 22px;
      font-weight: 600;
      letter-spacing: 0.12em;
      margin-bottom: 4px;
      background: linear-gradient(135deg, #c8b0ff, #80d0ff);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }
    #meritous-title-screen .merit-title-sub {
      text-align: center;
      font-size: 13px;
      color: rgba(180, 200, 230, 0.75);
      margin-bottom: 4px;
    }
    #meritous-title-screen .merit-title-btn,
    #merit-settings-overlay .merit-title-btn {
      width: 100%;
      padding: 12px 16px;
      border-radius: 10px;
      border: 1px solid rgba(140, 180, 255, 0.35);
      background: rgba(40, 60, 100, 0.45);
      color: #e8f4ff;
      font-family: inherit;
      font-size: 15px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
    }
    #meritous-title-screen .merit-title-btn:hover,
    #meritous-title-screen .merit-title-btn:focus-visible,
    #merit-settings-overlay .merit-title-btn:hover,
    #merit-settings-overlay .merit-title-btn:focus-visible {
      background: rgba(60, 100, 160, 0.55);
      border-color: rgba(160, 200, 255, 0.65);
      box-shadow: 0 0 16px rgba(100, 180, 255, 0.25);
      outline: none;
    }
    #meritous-title-screen .merit-title-btn.selected {
      border-color: rgba(180, 140, 255, 0.85);
      box-shadow: 0 0 20px rgba(140, 100, 255, 0.35);
      background: rgba(70, 50, 120, 0.55);
    }
    #meritous-title-screen .merit-title-btn.secondary,
    #merit-settings-overlay .merit-title-btn.secondary {
      background: transparent;
      border-color: rgba(140, 180, 255, 0.25);
      color: rgba(180, 210, 240, 0.85);
      font-size: 14px;
      padding: 10px;
    }
    #meritous-title-screen .merit-title-input {
      width: 100%;
      padding: 11px 14px;
      border-radius: 8px;
      border: 1px solid rgba(140, 180, 255, 0.35);
      background: rgba(0, 0, 0, 0.35);
      color: #e8f4ff;
      font-family: inherit;
      font-size: 15px;
    }
    #meritous-title-screen .merit-title-input:focus {
      outline: none;
      border-color: rgba(160, 200, 255, 0.7);
      box-shadow: 0 0 12px rgba(100, 180, 255, 0.2);
    }
    #meritous-title-screen .merit-title-row {
      display: flex;
      gap: 10px;
    }
    #meritous-title-screen .merit-title-row .merit-title-btn {
      flex: 1;
    }
    #meritous-title-screen .merit-title-error {
      color: #ff9090;
      font-size: 13px;
      min-height: 18px;
      text-align: center;
    }
    #meritous-title-screen .merit-title-loading-msg {
      text-align: center;
      font-size: 14px;
      color: rgba(200, 220, 255, 0.85);
      margin-bottom: 8px;
    }
    #meritous-title-screen .merit-title-loading-track {
      height: 6px;
      border-radius: 3px;
      background: rgba(0, 0, 0, 0.4);
      overflow: hidden;
      border: 1px solid rgba(140, 180, 255, 0.25);
    }
    #meritous-title-screen .merit-title-loading-fill {
      height: 100%;
      width: 0%;
      border-radius: 3px;
      background: linear-gradient(90deg, #8060ff, #40c0ff);
      transition: width 0.12s ease-out;
    }
    #meritous-title-screen .merit-settings-modal,
    #merit-settings-overlay {
      position: absolute;
      inset: 0;
      z-index: 5;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(4, 2, 12, 0.55);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      color: #e8f0ff;
    }
    #merit-settings-overlay {
      position: fixed;
      z-index: 100;
    }
    #meritous-title-screen .merit-settings-panel,
    #merit-settings-overlay .merit-settings-panel {
      width: min(440px, calc(100vw - 32px));
      max-height: min(80vh, 520px);
      overflow-y: auto;
    }
    #meritous-title-screen .merit-settings-row,
    #merit-settings-overlay .merit-settings-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 8px 0;
      border-bottom: 1px solid rgba(140, 180, 255, 0.12);
    }
    #meritous-title-screen .merit-settings-row:last-of-type,
    #merit-settings-overlay .merit-settings-row:last-of-type {
      border-bottom: none;
    }
    #meritous-title-screen .merit-settings-toggle,
    #merit-settings-overlay .merit-settings-toggle {
      min-width: 64px;
      padding: 6px 14px;
      border-radius: 20px;
      border: 1px solid rgba(140, 180, 255, 0.35);
      background: rgba(40, 60, 100, 0.4);
      color: #ccc;
      font-family: inherit;
      font-size: 13px;
      cursor: pointer;
    }
    #meritous-title-screen .merit-settings-toggle.on,
    #merit-settings-overlay .merit-settings-toggle.on {
      background: rgba(60, 120, 180, 0.55);
      border-color: rgba(100, 200, 255, 0.6);
      color: #e8f4ff;
    }
  `;
  document.head.appendChild(style);
}

export function ensureTitleScreenStyles(): void {
  injectStyles();
}

export function initTitleShell(): void {
  if (shell) return;
  injectStyles();
  setCanvasVisible(false);

  shell = document.createElement('div');
  shell.id = 'meritous-title-screen';

  const img = document.createElement('img');
  img.className = 'merit-splash-bg';
  img.src = SPLASH_URL;
  img.alt = '';
  img.draggable = false;
  shell.appendChild(img);

  const vignette = document.createElement('div');
  vignette.className = 'merit-splash-vignette';
  shell.appendChild(vignette);

  versionEl = document.createElement('div');
  versionEl.className = 'merit-title-version';
  versionEl.textContent = MERITOUS_VERSION;
  shell.appendChild(versionEl);

  panelHost = document.createElement('div');
  panelHost.className = 'merit-title-panel-host';
  panel = document.createElement('div');
  panel.className = 'merit-title-panel';
  panelHost.appendChild(panel);
  shell.appendChild(panelHost);

  document.body.appendChild(shell);
  setTitleLoading(0, 'Loading...');
}

export function getTitlePanel(): HTMLDivElement {
  if (!panel) initTitleShell();
  return panel!;
}

export function clearTitlePanel(): void {
  if (panel) panel.innerHTML = '';
}

export function setTitleLoading(progress: number, message: string): void {
  if (!panel) initTitleShell();
  if (!loadingWrap) {
    clearTitlePanel();
    loadingWrap = document.createElement('div');
    loadingMsg = document.createElement('div');
    loadingMsg.className = 'merit-title-loading-msg';
    const track = document.createElement('div');
    track.className = 'merit-title-loading-track';
    loadingBar = document.createElement('div');
    loadingBar.className = 'merit-title-loading-fill';
    track.appendChild(loadingBar);
    loadingWrap.appendChild(loadingMsg);
    loadingWrap.appendChild(track);
    panel!.appendChild(loadingWrap);
  }
  loadingMsg!.textContent = message;
  loadingBar!.style.width = `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%`;
}

export function clearTitleLoading(): void {
  loadingWrap = null;
  loadingBar = null;
  loadingMsg = null;
  clearTitlePanel();
}

export function hideTitleShell(): void {
  if (shell) shell.style.display = 'none';
  setCanvasVisible(true);
}

export function showTitleShell(): void {
  if (!shell) initTitleShell();
  shell!.style.display = '';
  setCanvasVisible(false);
}

export function getTitleShell(): HTMLDivElement | null {
  return shell;
}

export function mkTitleInput(placeholder: string, type: string): HTMLInputElement {
  const i = document.createElement('input');
  i.className = 'merit-title-input';
  i.placeholder = placeholder;
  i.type = type;
  return i;
}

export function mkTitleBtn(label: string, className = ''): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `merit-title-btn${className ? ` ${className}` : ''}`;
  b.textContent = label;
  return b;
}

export function showMainMenu(opts: MainMenuOptions): Promise<MenuChoice> {
  if (!shell) initTitleShell();
  clearTitleLoading();

  return new Promise((resolve) => {
    const p = getTitlePanel();
    p.innerHTML = '';

    const accountLabel =
      opts.saveMode === 'cloud' && opts.user ? `Account: ${opts.user}` : 'Playing offline';
    const sub = document.createElement('div');
    sub.className = 'merit-title-sub';
    sub.textContent = accountLabel;
    p.appendChild(sub);

    const items: { choice: MenuChoice; label: string }[] = [];
    if (opts.canContinue) items.push({ choice: 'continue', label: 'Continue' });
    items.push({ choice: 'new', label: 'New Game' });
    items.push({ choice: 'wuss', label: 'New Game (Wuss mode)' });
    items.push({ choice: 'settings', label: 'Settings' });
    if (opts.saveMode === 'cloud') items.push({ choice: 'logout', label: 'Logout' });

    const buttons: HTMLButtonElement[] = [];
    let selected = 0;

    const updateSelection = () => {
      for (let i = 0; i < buttons.length; i++) {
        buttons[i].classList.toggle('selected', i === selected);
      }
      buttons[selected]?.focus();
    };

    const pick = (choice: MenuChoice) => {
      cleanup();
      resolve(choice);
    };

    for (const item of items) {
      const btn = mkTitleBtn(item.label);
      btn.addEventListener('click', () => pick(item.choice));
      buttons.push(btn);
      p.appendChild(btn);
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        selected = Math.max(0, selected - 1);
        updateSelection();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        selected = Math.min(buttons.length - 1, selected + 1);
        updateSelection();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        pick(items[selected].choice);
      }
    };

    const cleanup = () => {
      window.removeEventListener('keydown', onKey);
    };

    window.addEventListener('keydown', onKey);
    updateSelection();
  });
}
